import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showErrorMessage } from '~/domain/utils';
import { useAppSelector } from '~/hooks';
import { PAYMENT_METHODS_QUERY_KEY } from '../factories';
import { paymentsService } from '../paymentsService';
import {
  IAddCardPayload,
  IConnectPlaidPayload,
  IFinalizePlaidVerifyPayload,
  IMintVerifyLinkTokenPayload,
} from '../types';

export const paymentMutationKeys = {
  mintConnectLinkToken: ['mintConnectLinkToken'] as const,
  connectPlaid: ['connectPlaid'] as const,
  mintVerifyLinkToken: ['mintVerifyLinkToken'] as const,
  finalizePlaidVerify: ['finalizePlaidVerify'] as const,
  addCard: ['addCard'] as const,
  deletePaymentMethod: ['deletePaymentMethod'] as const,
  selectPaymentMethod: ['selectPaymentMethod'] as const,
  deleteDefaultPaymentMethod: ['deleteDefaultPaymentMethod'] as const,
};

const useInvalidatePaymentMethods = () => {
  const queryClient = useQueryClient();

  return () => queryClient.invalidateQueries({ queryKey: [PAYMENT_METHODS_QUERY_KEY] });
};

const usePaymentUserContext = () => {
  const user = useAppSelector((state) => state.user.user);
  const selectedChildOffice = useAppSelector((state) => state.user.selectedChildOffice);

  return { user, selectedChildOffice };
};

export const useMintConnectLinkToken = () =>
  useMutation({
    mutationKey: paymentMutationKeys.mintConnectLinkToken,
    mutationFn: () => paymentsService.mintConnectLinkToken(),
    onError: (error) => showErrorMessage({ error }),
  });

export const useConnectPlaid = () => {
  const invalidatePaymentMethods = useInvalidatePaymentMethods();
  const { user, selectedChildOffice } = usePaymentUserContext();

  return useMutation({
    mutationKey: paymentMutationKeys.connectPlaid,
    mutationFn: (payload: IConnectPlaidPayload) =>
      paymentsService.connectPlaid({ payload, user, selectedChildOffice }),
    onSuccess: async () => {
      await invalidatePaymentMethods();
    },
    onError: (error) => showErrorMessage({ error }),
  });
};

export const useMintVerifyLinkToken = () =>
  useMutation({
    mutationKey: paymentMutationKeys.mintVerifyLinkToken,
    mutationFn: (payload: IMintVerifyLinkTokenPayload) =>
      paymentsService.mintVerifyLinkToken(payload),
    onError: (error) => showErrorMessage({ error }),
  });

export const useFinalizePlaidVerify = () => {
  const invalidatePaymentMethods = useInvalidatePaymentMethods();

  return useMutation({
    mutationKey: paymentMutationKeys.finalizePlaidVerify,
    mutationFn: (payload: IFinalizePlaidVerifyPayload) =>
      paymentsService.finalizePlaidVerify(payload),
    onSuccess: () => invalidatePaymentMethods(),
    onError: (error) => showErrorMessage({ error }),
  });
};

export const useAddCard = () => {
  const invalidatePaymentMethods = useInvalidatePaymentMethods();
  const { user, selectedChildOffice } = usePaymentUserContext();

  return useMutation({
    mutationKey: paymentMutationKeys.addCard,
    mutationFn: (payload: IAddCardPayload) =>
      paymentsService.addCard({ payload, user, selectedChildOffice }),
    onSuccess: async () => {
      await invalidatePaymentMethods();
    },
    onError: (error) => showErrorMessage({ error }),
  });
};

export const useDeletePaymentMethod = () => {
  const invalidatePaymentMethods = useInvalidatePaymentMethods();

  return useMutation({
    mutationKey: paymentMutationKeys.deletePaymentMethod,
    mutationFn: (paymentId: string) => paymentsService.deletePaymentMethod(paymentId),
    onSuccess: () => invalidatePaymentMethods(),
    onError: (error) => showErrorMessage({ error }),
  });
};

export const useSelectPaymentMethod = () => {
  const invalidatePaymentMethods = useInvalidatePaymentMethods();

  return useMutation({
    mutationKey: paymentMutationKeys.selectPaymentMethod,
    mutationFn: (paymentId: string) => paymentsService.selectPaymentMethod(paymentId),
    onSuccess: async () => {
      await invalidatePaymentMethods();
    },
    onError: (error) => showErrorMessage({ error }),
  });
};

export const useDeleteDefaultPaymentMethod = () => {
  const invalidatePaymentMethods = useInvalidatePaymentMethods();

  return useMutation({
    mutationKey: paymentMutationKeys.deleteDefaultPaymentMethod,
    mutationFn: async ({
      oldPaymentId,
      newPaymentId,
    }: {
      oldPaymentId: string;
      newPaymentId: string;
    }) => {
      await paymentsService.selectPaymentMethod(newPaymentId);
      await paymentsService.deletePaymentMethod(oldPaymentId);
    },
    onSuccess: async () => {
      await invalidatePaymentMethods();
    },
    onError: (error) => showErrorMessage({ error }),
  });
};
